// Generat automat din README.md
import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Eye, Zap, Brain, Target, Globe, Activity, Filter, Download, Bell, RefreshCw, Search, MoreVertical, TrendingUp, Users, Lock } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const DetectareAmenintari = () => {
  const [threatLevel, setThreatLevel] = useState('medium');
  const [autoScan, setAutoScan] = useState(true);
  const [showThreatModal, setShowThreatModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedThreat, setSelectedThreat] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('all');

  // Real-time threat detection simulation
  useEffect(() => {
    if (!autoScan) return;
    const interval = setInterval(() => {
      // Simulate real-time threat detection
      console.log('AI Threat Detection Scan Running...');
    }, 15000); // 15 seconds scan cycle

    return () => clearInterval(interval);
  }, [autoScan]);

  // Mock data pentru detectarea amenințărilor
  const threatData = {
    overview: {
      activeThreats: 12,
      blockedAttacks: 156,
      aiConfidence: 94.2,
      lastScan: new Date().toLocaleTimeString('ro-RO'),
      systemHealth: 'Secure',
      riskScore: 23 // Low risk = 0-30, Medium = 31-70, High = 71-100
    },
    threatCategories: [
      {
        category: 'Malware',
        detected: 23,
        blocked: 21,
        active: 2,
        trend: 'decreasing',
        riskLevel: 'Medium',
        description: 'Virus, worms, ransomware și alte programe malware'
      },
      {
        category: 'Phishing',
        detected: 45,
        blocked: 43,
        active: 2,
        trend: 'increasing',
        riskLevel: 'High',
        description: 'Încercări de obținere credențiale prin înșelăciune'
      },
      {
        category: 'DDoS',
        detected: 8,
        blocked: 8,
        active: 0,
        trend: 'stable',
        riskLevel: 'Low',
        description: 'Atacuri distribuite de negare a serviciului'
      },
      {
        category: 'Intrusion',
        detected: 15,
        blocked: 12,
        active: 3,
        trend: 'increasing',
        riskLevel: 'High',
        description: 'Tentative de acces neautorizat în sistem'
      },
      {
        category: 'Data Breach',
        detected: 3,
        blocked: 3,
        active: 0,
        trend: 'stable',
        riskLevel: 'Critical',
        description: 'Încercări de furt sau compromitere date'
      }
    ],
    activeThreats: [
      {
        id: 'THR-2024-001',
        type: 'Advanced Persistent Threat',
        severity: 'Critical',
        source: '185.234.125.67',
        target: 'Database Server #2',
        detected: '2024-03-15 14:32:15',
        status: 'Active',
        aiConfidence: 96,
        description: 'Activitate suspectă detectată - posibilă exfiltrare de date în progres',
        indicators: [
          'Trafic criptat neobișnuit către servere externe',
          'Accese repetate la baze de date sensibile',
          'Pattern de comportament atipic pentru utilizatorul compromis'
        ],
        mitigationActions: [
          'Izolare automată a segmentului de rețea afectat',
          'Blocare IP sursă în toate sistemele firewall',
          'Alertare echipa de incident response'
        ],
        impactScore: 9.2,
        lastActivity: '2024-03-15 14:45:23'
      },
      {
        id: 'THR-2024-002',
        type: 'Malware - Ransomware',
        severity: 'High',
        source: 'Email Gateway',
        target: 'Workstation Network',
        detected: '2024-03-15 13:15:42',
        status: 'Contained',
        aiConfidence: 91,
        description: 'Ransomware detectat în attachment email - automat izolat',
        indicators: [
          'Signature match cu familie Ryuk ransomware',
          'Comportament de criptare fișiere detectat',
          'Comunicare C&C către domenii cunoscute malware'
        ],
        mitigationActions: [
          'Fișier în carantină automată',
          'Workstation izolată din rețea',
          'Scan complet sistemelor adiacente'
        ],
        impactScore: 7.8,
        lastActivity: '2024-03-15 13:20:15'
      },
      {
        id: 'THR-2024-003',
        type: 'Phishing Campaign',
        severity: 'Medium',
        source: 'Multiple IPs',
        target: 'Email Users',
        detected: '2024-03-15 12:08:30',
        status: 'Monitoring',
        aiConfidence: 87,
        description: 'Campanie phishing țintită pe angajații din departamentul financiar',
        indicators: [
          'Emailuri cu subiect similar despre facturi false',
          'Link-uri către site-uri spoofed de bănci',
          'Presiune temporală în mesaje pentru urgență'
        ],
        mitigationActions: [
          'Alertă automată către utilizatori vizați',
          'Blocare domenii malware în email gateway',
          'Training de awareness programat'
        ],
        impactScore: 5.4,
        lastActivity: '2024-03-15 12:35:47'
      },
      {
        id: 'THR-2024-004',
        type: 'Insider Threat',
        severity: 'High',
        source: 'Internal User: j.popescu',
        target: 'Confidential Documents',
        detected: '2024-03-15 11:45:18',
        status: 'Investigating',
        aiConfidence: 78,
        description: 'Comportament anomal detectat - acces în afara orelor la documente sensibile',
        indicators: [
          'Acces la documente în afara programului de lucru',
          'Download volume mari de date confidențiale',
          'Pattern de acces diferit de comportamentul normal'
        ],
        mitigationActions: [
          'Monitorizare sporită a activității utilizatorului',
          'Alertare departament HR și legal',
          'Log forenzic complet al activităților'
        ],
        impactScore: 8.1,
        lastActivity: '2024-03-15 11:58:32'
      }
    ],
    aiModels: [
      {
        name: 'Network Anomaly Detection',
        type: 'Deep Learning',
        accuracy: 94.2,
        lastTraining: '2024-03-10',
        status: 'Active',
        detections: 156,
        falsePositives: 8
      },
      {
        name: 'Malware Classification',
        type: 'Random Forest',
        accuracy: 97.8,
        lastTraining: '2024-03-12',
        status: 'Active',
        detections: 89,
        falsePositives: 3
      },
      {
        name: 'Behavioral Analytics',
        type: 'Ensemble',
        accuracy: 91.5,
        lastTraining: '2024-03-08',
        status: 'Training',
        detections: 234,
        falsePositives: 12
      }
    ],
    threatIntelligence: [
      {
        source: 'MISP Threat Sharing',
        indicators: 1247,
        lastUpdate: '2024-03-15 10:30:00',
        status: 'Synced',
        newThreats: 23
      },
      {
        source: 'Commercial Threat Feed',
        indicators: 5432,
        lastUpdate: '2024-03-15 09:15:00',
        status: 'Synced',
        newThreats: 67
      },
      {
        source: 'Government CERT',
        indicators: 892,
        lastUpdate: '2024-03-15 08:00:00',
        status: 'Synced',
        newThreats: 12
      }
    ]
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'error';
      case 'Contained': return 'warning';
      case 'Monitoring': return 'info';
      case 'Investigating': return 'warning';
      case 'Resolved': return 'success';
      default: return 'default';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increasing': return <TrendingUp className="w-3 h-3 text-red-500" />;
      case 'decreasing': return <TrendingUp className="w-3 h-3 text-green-500 rotate-180" />;
      case 'stable': return <Activity className="w-3 h-3 text-gray-500" />;
      default: return null;
    }
  };

  const filteredThreats = threatData.activeThreats.filter(threat => {
    const matchesSearch = threat.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         threat.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         threat.source.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = selectedSeverity === 'all' || threat.severity === selectedSeverity;
    return matchesSearch && matchesSeverity;
  });

  const threatColumns = [
    { 
      header: 'ID Amenințare', 
      key: 'id',
      render: (value, row) => (
        <div>
          <div className="font-mono text-sm font-medium">{value}</div>
          <div className="text-xs text-gray-500">{row.detected}</div>
        </div>
      )
    },
    { 
      header: 'Tip', 
      key: 'type',
      render: (value) => <div className="font-medium text-gray-900 dark:text-white">{value}</div>
    },
    { 
      header: 'Severitate', 
      key: 'severity',
      render: (value) => <Badge variant={getSeverityColor(value)}>{value}</Badge>
    },
    { 
      header: 'Sursă', 
      key: 'source',
      render: (value) => <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{value}</code>
    },
    { 
      header: 'Țintă', 
      key: 'target'
    },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge>
    },
    { 
      header: 'AI Confidence', 
      key: 'aiConfidence',
      render: (value) => (
        <div className="flex items-center space-x-2">
          <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${
                value > 90 ? 'bg-green-500' : value > 70 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${value}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium">{value}%</span>
        </div>
      )
    },
    { 
      header: 'Impact', 
      key: 'impactScore',
      render: (value) => (
        <span className={`font-semibold ${
          value > 8 ? 'text-red-600' : value > 6 ? 'text-yellow-600' : 'text-green-600'
        }`}>
          {value}/10
        </span>
      )
    },
    {
      header: 'Acțiuni',
      key: 'actions',
      render: (_, row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setSelectedThreat(row);
              setShowThreatModal(true);
            }}
            className="text-primary-600 hover:text-primary-800 text-sm"
            title="Vezi Detalii"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button className="text-gray-600 hover:text-gray-800 text-sm" title="Opțiuni">
            <MoreVertical className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Detectare Amenințări AI</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Sistem avansat de detecție a amenințărilor cyber cu machine learning și analize comportamentale
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Brain className="w-4 h-4 mr-2" />
            Ultimul scan AI: {threatData.overview.lastScan}
            <span className="mx-2">•</span>
            <Shield className="w-4 h-4 mr-1" />
            <span className={`${threatData.overview.systemHealth === 'Secure' ? 'text-green-600' : 'text-red-600'}`}>
              Sistem {threatData.overview.systemHealth}
            </span>
          </div>
        </div>
        <div className="flex space-x-3">
          <button 
            className={`btn-secondary ${autoScan ? 'bg-primary-100 dark:bg-primary-800' : ''}`}
            onClick={() => setAutoScan(!autoScan)}
          >
            <Zap className={`w-4 h-4 mr-2 ${autoScan ? 'text-primary-600' : ''}`} />
            AI Auto Scan
          </button>
          <button className="btn-secondary">
            <Download className="w-4 h-4 mr-2" />
            Raport Amenințări
          </button>
          <button 
            className="btn-primary"
            onClick={() => setShowConfigModal(true)}
          >
            <Brain className="w-4 h-4 mr-2" />
            Configurare AI
          </button>
        </div>
      </div>

      {/* Threat Overview Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <Card title="Amenințări Active" subtitle="Necesită atenție" icon={AlertTriangle}>
          <div className="text-2xl font-bold text-red-600">{threatData.overview.activeThreats}</div>
          <div className="text-sm text-red-600">Real-time detection</div>
        </Card>
        <Card title="Atacuri Blocate" subtitle="Astăzi" icon={Shield}>
          <div className="text-2xl font-bold text-green-600">{threatData.overview.blockedAttacks}</div>
          <div className="text-sm text-green-600">+34% vs ieri</div>
        </Card>
        <Card title="AI Confidence" subtitle="Model accuracy" icon={Brain}>
          <div className="text-2xl font-bold text-primary-600">{threatData.overview.aiConfidence}%</div>
          <div className="text-sm text-blue-600">Machine learning</div>
        </Card>
        <Card title="Risk Score" subtitle="General system" icon={Target}>
          <div className="flex items-center space-x-3">
            <div className="text-2xl font-bold text-green-600">{threatData.overview.riskScore}/100</div>
            <div className={`px-2 py-1 rounded text-xs font-medium ${
              threatData.overview.riskScore <= 30 ? 'bg-green-100 text-green-800' :
              threatData.overview.riskScore <= 70 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
            }`}>
              {threatData.overview.riskScore <= 30 ? 'Low Risk' :
               threatData.overview.riskScore <= 70 ? 'Medium Risk' : 'High Risk'}
            </div>
          </div>
        </Card>
        <Card title="System Status" subtitle="Overall health" icon={Activity}>
          <div className="flex items-center space-x-3">
            <div className={`w-4 h-4 rounded-full ${
              threatData.overview.systemHealth === 'Secure' ? 'bg-green-500 animate-pulse' : 'bg-red-500 animate-pulse'
            }`}></div>
            <div className="text-lg font-bold text-green-600">{threatData.overview.systemHealth}</div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Active Threats Table */}
        <div className="lg:col-span-3">
          <Card title="Amenințări Active Detectate" subtitle="Machine learning și behavioral analytics">
            <div className="mb-4 flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Caută amenințare sau IP..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <select 
                value={selectedSeverity} 
                onChange={(e) => setSelectedSeverity(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              >
                <option value="all">Toate severitățile</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
              <button className="btn-secondary">
                <Filter className="w-4 h-4 mr-2" />
                Filtre AI
              </button>
            </div>
            <Table columns={threatColumns} data={filteredThreats} />
            <div className="mt-4 text-sm text-gray-500">
              AI detectează și clasifică automat amenințările în timp real folosind {threatData.aiModels.length} modele ML active.
            </div>
          </Card>
        </div>

        {/* Threat Categories Sidebar */}
        <div className="space-y-6">
          <Card title="Categorii Amenințări" icon={Target}>
            <div className="space-y-4">
              {threatData.threatCategories.map((category, index) => (
                <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                      {category.category}
                    </div>
                    <div className="flex items-center space-x-2">
                      {getTrendIcon(category.trend)}
                      <Badge variant={getRiskColor(category.riskLevel)} className="text-xs">
                        {category.riskLevel}
                      </Badge>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                    <div className="text-center">
                      <div className="font-semibold text-blue-600">{category.detected}</div>
                      <div className="text-gray-500">Detectate</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-green-600">{category.blocked}</div>
                      <div className="text-gray-500">Blocate</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-red-600">{category.active}</div>
                      <div className="text-gray-500">Active</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {category.description}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card title="AI Models Status" icon={Brain}>
            <div className="space-y-3">
              {threatData.aiModels.map((model, index) => (
                <div key={index} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                      {model.name}
                    </div>
                    <Badge variant={model.status === 'Active' ? 'success' : 'warning'} className="text-xs">
                      {model.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    {model.type} • Antrenat: {model.lastTraining}
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <div>
                      <span className="text-green-600 font-medium">{model.accuracy}%</span>
                      <span className="text-gray-500"> accuracy</span>
                    </div>
                    <div>
                      <span className="font-medium">{model.detections}</span>
                      <span className="text-gray-500"> detections</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button className="w-full mt-4 btn-primary btn-sm">
              <Brain className="w-3 h-3 mr-1" />
              Retrain Models
            </button>
          </Card>
        </div>
      </div>

      {/* Threat Intelligence */}
      <Card title="Threat Intelligence Feeds" subtitle="Integrare automată cu surse externe de threat intelligence">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {threatData.threatIntelligence.map((feed, index) => (
            <div key={index} className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-green-800 dark:text-green-200">{feed.source}</h4>
                <Badge variant={feed.status === 'Synced' ? 'success' : 'warning'} className="text-xs">
                  {feed.status}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-green-600 font-semibold">{feed.indicators}</div>
                  <div className="text-green-700 dark:text-green-300 text-xs">Indicators total</div>
                </div>
                <div>
                  <div className="text-blue-600 font-semibold">+{feed.newThreats}</div>
                  <div className="text-blue-700 dark:text-blue-300 text-xs">Amenințări noi</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-green-600 dark:text-green-400">
                Last update: {new Date(feed.lastUpdate).toLocaleString('ro-RO')}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Threat Detail Modal */}
      <Modal
        isOpen={showThreatModal}
        onClose={() => {
          setShowThreatModal(false);
          setSelectedThreat(null);
        }}
        title={`Detalii Amenințare: ${selectedThreat?.id}`}
        size="large"
      >
        {selectedThreat && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Informații Generale</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">ID:</span>
                    <span className="font-mono">{selectedThreat.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Tip:</span>
                    <span>{selectedThreat.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Severitate:</span>
                    <Badge variant={getSeverityColor(selectedThreat.severity)}>{selectedThreat.severity}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Sursă:</span>
                    <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{selectedThreat.source}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Țintă:</span>
                    <span>{selectedThreat.target}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Status:</span>
                    <Badge variant={getStatusColor(selectedThreat.status)}>{selectedThreat.status}</Badge>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">AI Analysis</h3>
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                      Confidence Score
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${selectedThreat.aiConfidence}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-semibold text-blue-600">{selectedThreat.aiConfidence}%</span>
                    </div>
                  </div>
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                      Impact Score
                    </div>
                    <div className="text-2xl font-bold text-red-600">{selectedThreat.impactScore}/10</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Descriere</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                {selectedThreat.description}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Indicatori de Compromitere</h3>
                <div className="space-y-2">
                  {selectedThreat.indicators.map((indicator, index) => (
                    <div key={index} className="flex items-start space-x-2 text-sm">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-600 dark:text-gray-400">{indicator}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Acțiuni de Mitigare</h3>
                <div className="space-y-2">
                  {selectedThreat.mitigationActions.map((action, index) => (
                    <div key={index} className="flex items-start space-x-2 text-sm">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-600 dark:text-gray-400">{action}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                <span className="font-medium text-yellow-800 dark:text-yellow-200">Ultima Activitate</span>
              </div>
              <div className="text-sm text-yellow-700 dark:text-yellow-300">
                {selectedThreat.lastActivity}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button className="btn-secondary">Export Threat Report</button>
              <button className="btn-secondary">Escalate to SOC</button>
              <button className="btn-primary">Mark as Resolved</button>
            </div>
          </div>
        )}
      </Modal>

      {/* AI Configuration Modal */}
      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title="Configurare AI Threat Detection"
        size="large"
      >
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Model Configuration</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Detection Sensitivity
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white">
                  <option>High (More false positives)</option>
                  <option>Medium (Balanced)</option>
                  <option>Low (Fewer false positives)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Scan Frequency
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white">
                  <option>Real-time</option>
                  <option>Every 15 seconds</option>
                  <option>Every minute</option>
                  <option>Every 5 minutes</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">AI Model Selection</h3>
            <div className="space-y-3">
              {threatData.aiModels.map((model, index) => (
                <div key={index} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <input type="checkbox" className="rounded" defaultChecked={model.status === 'Active'} />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{model.name}</div>
                      <div className="text-xs text-gray-500">{model.type} • {model.accuracy}% accuracy</div>
                    </div>
                  </div>
                  <Badge variant={model.status === 'Active' ? 'success' : 'warning'} className="text-xs">
                    {model.status}
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Threshold Settings</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Critical Alert Threshold
                </label>
                <input
                  type="range"
                  min="70"
                  max="100"
                  defaultValue="90"
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>70%</span>
                  <span>100%</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Auto-Block Confidence
                </label>
                <input
                  type="range"
                  min="80"
                  max="100"
                  defaultValue="95"
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>80%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Integration Settings</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Auto-response to Critical Threats</span>
                <input type="checkbox" className="rounded" defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Real-time SIEM Integration</span>
                <input type="checkbox" className="rounded" defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Threat Intelligence Auto-sync</span>
                <input type="checkbox" className="rounded" defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Machine Learning Auto-retrain</span>
                <input type="checkbox" className="rounded" defaultChecked />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button 
              className="btn-secondary"
              onClick={() => setShowConfigModal(false)}
            >
              Anulează
            </button>
            <button className="btn-secondary">
              Test Configuration
            </button>
            <button className="btn-primary">
              <Brain className="w-4 h-4 mr-2" />
              Salvează & Deploy
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DetectareAmenintari;